import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { SparkButton } from 'sharedComponents/UI';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { useGetBlobSasToken } from 'hooks/useGetBlobSasToken';
import { useWorkspaceStore } from 'stores';

interface AudioPlayerProps {
  audioUrl: string;
  onError?: (message: string) => void;
  className?: string;
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  audioUrl,
  onError,
  className,
}) => {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [currentTime, setCurrentTime] = React.useState(0);
  const [duration, setDuration] = React.useState(0);
  const [isLoaded, setIsLoaded] = React.useState(false);
  const [playbackRate, setPlaybackRate] = React.useState(1);
  const audioRef = React.useRef<HTMLAudioElement>(null);

  const [audioFileURL, setAudioFileURL] = React.useState(audioUrl);
  const { workspace } = useWorkspaceStore();
  const { refetch: fetchToken } = useGetBlobSasToken(
    workspace?.attributes?.containerName || ''
  );
  React.useEffect(() => {
    fetchToken().then((data) => {
      const token = data.data?.data.attributes.token;
      setAudioFileURL(`${audioUrl}?${token}`);
    });
  }, [fetchToken, audioFileURL]);

  // Playback speed options
  const speedOptions = [0.5, 0.75, 1, 1.25, 1.5, 2];

  // Generate mock waveform data
  const waveformData = React.useMemo(() => {
    return Array.from({ length: 80 }, () => Math.random() * 0.8 + 0.2);
  }, []);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    const handleError = () => {
      onError?.('Failed to load audio file');
      setIsLoaded(false);
    };

    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
    };
  }, [audioUrl, onError]);

  // Update playback rate when it changes
  React.useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.playbackRate = playbackRate;
    }
  }, [playbackRate]);

  // Reset state when audio URL changes
  React.useEffect(() => {
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setIsLoaded(false);
    setPlaybackRate(1);
  }, [audioUrl]);

  const handlePlayPause = () => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play().catch((error) => {
        onError?.('Failed to play audio');
        console.error('Audio play error:', error);
      });
    }
    setIsPlaying(!isPlaying);
  };

  const handleWaveformClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !isLoaded) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleSpeedChange = () => {
    const currentIndex = speedOptions.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speedOptions.length;
    setPlaybackRate(speedOptions[nextIndex]);
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (!audioUrl) {
    return (
      <Box className={className}>
        <Typography variant='body2' color='text.secondary'>
          No audio file provided
        </Typography>
      </Box>
    );
  }

  return (
    <Box className={className}>
      <audio
        ref={audioRef}
        src={audioFileURL}
        preload='metadata'
        style={{ display: 'none' }}
      />

      <Box
        sx={{
          p: 2,
          backgroundColor: '#f8f9fa',
          borderRadius: 2,
          border: '1px solid #e9ecef',
        }}
      >
        {/* Main Player Controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            mb: 2,
          }}
        >
          {/* Play/Pause Button */}
          <IconButton
            onClick={handlePlayPause}
            disabled={!isLoaded}
            sx={{
              backgroundColor: (theme) => theme.palette.primary.main,
              color: 'white',
              width: 40,
              height: 40,
              '&:hover': {
                backgroundColor: (theme) => theme.palette.primary.dark,
              },
              '&:disabled': {
                backgroundColor: (theme) => theme.palette.primary.light,
                color: '#9ca3af',
              },
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>

          {/* Waveform Container */}
          <Box
            sx={{
              flex: 1,
              height: 40,
              position: 'relative',
              cursor: isLoaded ? 'pointer' : 'default',
            }}
            onClick={handleWaveformClick}
          >
            {/* Waveform Background */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                gap: '1px',
                opacity: isLoaded ? 1 : 0.3,
              }}
            >
              {waveformData.map((height, index) => (
                <Box
                  key={index}
                  sx={{
                    flex: 1,
                    height: `${height * 100}%`,
                    backgroundColor:
                      (index / waveformData.length) * 100 <= progressPercentage
                        ? (theme) => theme.palette.primary.main
                        : '#d1d5db',
                    borderRadius: '1px',
                    transition: 'background-color 0.1s ease',
                  }}
                />
              ))}
            </Box>

            {/* Progress Indicator */}
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: `${progressPercentage}%`,
                width: 2,
                height: '100%',
                backgroundColor: (theme) => theme.palette.primary.main,
                borderRadius: 1,
                transform: 'translateX(-50%)',
                opacity: isPlaying ? 1 : 0.7,
              }}
            />
          </Box>

          {/* Speed Control */}
          <SparkButton
            onClick={handleSpeedChange}
            disabled={!isLoaded}
            variant='contained'
            sx={{ borderRadius: '30px' }}
          >
            {playbackRate}x
          </SparkButton>
        </Box>

        {/* Time Display at Bottom Center */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Typography
            variant='body2'
            sx={{
              color: '#6b7280',
              fontFamily: 'monospace',
              fontSize: '0.875rem',
              textAlign: 'center',
            }}
          >
            {isLoaded ? formatTime(duration - currentTime) : '0:00'}
          </Typography>
        </Box>
      </Box>

      {!isLoaded && audioUrl && (
        <Typography
          variant='caption'
          sx={{
            display: 'block',
            textAlign: 'center',
            color: '#9ca3af',
            mt: 1,
          }}
        >
          Loading audio...
        </Typography>
      )}
    </Box>
  );
};

export default AudioPlayer;
